import { useCartDrawer } from '@iheartjane/cart';
import {
  isSameStoreId,
  useLocalTags,
  getLocalDeliveryAddress,
  toDeliveryAddressParams,
  updateLocalDeliveryAddress,
} from '@iheartjane/cart/utils';
import type { Cart, CreateCartProps } from '@iheartjane/data-access';
import { ApiError, removeCartProduct } from '@iheartjane/data-access';
import {
  cartKeys,
  useAddCartProduct,
  useAddLoyaltyRedemption,
  useCreateCart,
  useDeleteCart,
  useRemoveCartProduct,
  useRemoveLoyaltyRedemption,
  useUpdateCart,
  useUpdateCartProduct,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { EventNames, trackError } from '@iheartjane/tracking';
import type { PriceId } from '@iheartjane/types';
import { useQueryClient } from '@tanstack/react-query';
import pick from 'lodash-es/pick';
import { useMemo } from 'react';

import { trackCartEvent } from '@jane/monolith/customer/cart';
import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import { selectApplication } from '@jane/monolith/customer/redux/application';
import type {
  AttemptToAddToCartPayload,
  CartState,
} from '@jane/monolith/customer/redux/cart';
import {
  CRM_REDEMPTION_ERROR_MESSAGE,
  applyCrmRedemption,
  attemptToAddToCart,
  checkout,
  deleteCartItem,
  deleteUnavailableProducts,
  maybeChangeStore,
  opensDrawerAfterAdd,
  removeCrmRedemption,
  removeDeliveryAddress,
  resetCart,
  selectCart,
  updateCart,
  updateDeliveryAddress,
  updateHeadlessDeliveryAddress,
  updateReservationMode,
} from '@jane/monolith/customer/redux/cart';
import { selectEmbeddedApp } from '@jane/monolith/customer/redux/embeddedApp';
import { selectStore } from '@jane/monolith/customer/redux/store';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { NotificationsService } from '@jane/monolith/services/notifications';
import { moeAddToBag, track } from '@jane/shared-ecomm/tracking';
import { postAnalyticsEvent } from '@jane/shared-ecomm/util';
import { getJwt } from '@jane/shared/auth';
import { useEcommApp } from '@jane/shared/providers';
import { getActualUnitPrice, prepareCartProduct } from '@jane/shared/util';

import { getLocalCartUuid, updateLocalCartUuid } from './localCartUuid';

/** What a v3 delete needs; v1 and v3 `unavailable_products` rows both fit. */
export interface UnavailableProductRef {
  price_id: PriceId;
  product_id: number;
}

/**
 * Cart writes: v3 mutations flag-on, v1 thunks flag-off. Actions without a v3
 * version yet use the v1 thunk either way.
 * TODO(carts-v3): move the remaining actions to v3.
 *
 * Flag-on, the only Redux left is `addProduct`'s store-switch guard. The
 * thunks' `bundlePossibilities` dispatches are dropped on purpose (ADR-0003).
 */
export interface CartFacadeActions {
  /**
   * Flag-on, creates the cart if there isn't one. No `onAdded`: the facade
   * opens the drawer, so pass `withoutDrawer` instead.
   */
  addProduct: (payload: Omit<AttemptToAddToCartPayload, 'onAdded'>) => void;
  applyLoyalty: (...args: Parameters<typeof applyCrmRedemption>) => void;
  checkout: (...args: Parameters<typeof checkout>) => void;
  /** Creates an empty cart. A no-op flag-off, since v1 creates on first add. */
  createCart: () => void;
  deleteUnavailableProducts: (
    products: readonly UnavailableProductRef[]
  ) => void;
  removeDeliveryAddress: () => void;
  removeLoyalty: (...args: Parameters<typeof removeCrmRedemption>) => void;
  removeProduct: (...args: Parameters<typeof deleteCartItem>) => void;
  resetCart: (...args: Parameters<typeof resetCart>) => void;
  /** Flag-on this only saves the address; the new query key refetches. */
  updateDeliveryAddress: (
    ...args: Parameters<typeof updateDeliveryAddress>
  ) => void;
  updateGroupSpecial: (groupSpecialId: number | undefined) => void;
  /** Flag-off, unlike `updateDeliveryAddress`, this skips the refetches. */
  updateHeadlessDeliveryAddress: (
    ...args: Parameters<typeof updateHeadlessDeliveryAddress>
  ) => void;
  updateProduct: (...args: Parameters<typeof updateCart>) => void;
  updateReservationMode: (
    ...args: Parameters<typeof updateReservationMode>
  ) => void;
}

// Stable default for trees that never loaded a cart.
const NO_PRODUCTS: CartState['cart']['products'] = [];

export const useCartActions = (): CartFacadeActions => {
  const dispatch = useCustomerDispatch();
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const { appMode } = useEcommApp();
  const { partnerId: storeId } = useCustomerSelector(selectEmbeddedApp);
  // The v3 cart's `store` is only `{ id }`; currency and name come from here.
  const { store } = useCustomerSelector(selectStore);
  const { close: closeCartDrawer, open: openCartDrawer } = useCartDrawer();
  // Flag-off uses these to close the drawer when a removal empties the cart.
  const v1CartProducts = useCustomerSelector(
    (state) => selectCart(state).cart?.products ?? NO_PRODUCTS
  );
  // Narrow selector: all of `selectCart` would re-render every consumer on any
  // cart change.
  const janeDeviceId = useCustomerSelector(
    (state) => selectApplication(state).janeDeviceId
  );
  const cartTags = useLocalTags(appMode, storeId);
  const queryClient = useQueryClient();

  // `mutate` is stable, so the memo below doesn't rebuild every render.
  const { mutate: mutateUpdateCart } = useUpdateCart();
  const { mutate: mutateDeleteCart } = useDeleteCart();
  const { mutate: mutateUpdateCartProduct } = useUpdateCartProduct();
  const { mutate: mutateRemoveCartProduct } = useRemoveCartProduct();
  const { mutate: mutateAddLoyalty } = useAddLoyaltyRedemption();
  const { mutate: mutateRemoveLoyalty } = useRemoveLoyaltyRedemption();
  const { mutate: mutateAddCartProduct } = useAddCartProduct();
  // Save the new uuid here, once, rather than at each call site.
  const { mutate: mutateCreateCart } = useCreateCart({
    onSuccess: (cart) => updateLocalCartUuid(appMode, storeId, cart.uuid),
  });

  return useMemo(() => {
    const openDrawerAfterAdd = (withoutDrawer?: boolean) => {
      if (opensDrawerAfterAdd(appMode, withoutDrawer)) openCartDrawer();
    };

    const v1Actions: CartFacadeActions = {
      addProduct: (payload) =>
        dispatch(
          attemptToAddToCart({
            ...payload,
            onAdded: () => openDrawerAfterAdd(payload.withoutDrawer),
          })
        ),
      updateProduct: (...args) => dispatch(updateCart(...args)),
      removeProduct: (productId, priceId) => {
        if (v1CartProducts.length === 1 && v1CartProducts[0].id === productId) {
          closeCartDrawer();
        }
        dispatch(deleteCartItem(productId, priceId));
      },
      updateReservationMode: (...args) =>
        dispatch(updateReservationMode(...args)),
      updateGroupSpecial: () => undefined,
      updateDeliveryAddress: (...args) =>
        dispatch(updateDeliveryAddress(...args)),
      removeDeliveryAddress: () => dispatch(removeDeliveryAddress()),
      updateHeadlessDeliveryAddress: (...args) =>
        dispatch(updateHeadlessDeliveryAddress(...args)),
      applyLoyalty: (...args) => dispatch(applyCrmRedemption(...args)),
      removeLoyalty: (...args) => dispatch(removeCrmRedemption(...args)),
      deleteUnavailableProducts: (products) => {
        if (!v1CartProducts.length) closeCartDrawer();
        dispatch(
          deleteUnavailableProducts(
            products.map((product) => product.product_id)
          )
        );
      },
      checkout: (...args) => dispatch(checkout(...args)),
      resetCart: (...args) => {
        closeCartDrawer();
        dispatch(resetCart(...args));
      },
      // Don't dispatch `initiateCreateCart`: only the headless create clears
      // the `isLoading` it sets, so it would stay stuck true.
      createCart: () => undefined,
    };

    if (!useV3Cart) return v1Actions;

    // The key `useCartState` observes; PATCHes send the same address so their
    // responses land on it.
    const activeAddress = () =>
      toDeliveryAddressParams(getLocalDeliveryAddress());
    const cartQueryKey = (uuid: string) =>
      cartKeys.detail(uuid, undefined, activeAddress());

    // Read from the cache, not a `useCart` observer, which would re-render
    // every consumer of this hook.
    const cartSnapshot = (uuid: string) =>
      queryClient.getQueryData<Cart>(cartQueryKey(uuid)) ?? null;

    // Always refetch: the server may have destroyed the cart, and a 204 can't
    // tell us. A destroyed cart 404s, which `useCartState` handles.
    const settleAfterRemoval = (uuid: string) =>
      queryClient.invalidateQueries({ queryKey: cartKeys.detail(uuid) });

    // Without a uuid, `/v3/carts//cart_products` 404s, so a first add creates
    // the cart with the product. No `reservation_mode`: the server derives it.
    const createV3Cart = (
      products: CreateCartProps['products'],
      newCartStoreId: number | string,
      onCreated?: (cart: Cart) => void
    ) =>
      mutateCreateCart(
        {
          appMode,
          deviceId: janeDeviceId,
          jwt: getJwt(),
          products,
          storeId: newCartStoreId,
          tags: cartTags,
        },
        { onSuccess: onCreated }
      );

    const updateProduct: CartFacadeActions['updateProduct'] = (
      { count, price_id, product_id },
      moeTrack
    ) => {
      const uuid = getLocalCartUuid(appMode, storeId);
      const cart = uuid ? cartSnapshot(uuid) : null;
      if (!uuid || !cart) return;

      const cartProduct = cart.products.find(
        (product) =>
          product.id === product_id || product.product_id === product_id
      );
      const changeInCount = (count ?? 0) - (cartProduct?.count ?? 0);

      mutateUpdateCartProduct(
        {
          appMode,
          cartUuid: uuid,
          count,
          jwt: getJwt(),
          priceId: price_id,
          productId: Number(product_id),
        },
        {
          onSuccess: () => {
            if (changeInCount <= 0 || !cartProduct) return;
            moeTrack(
              moeAddToBag({
                cartUuid: uuid,
                product: { ...cartProduct, count: changeInCount },
                storeId: store?.id ?? null,
                storeName: store?.name ?? null,
              })
            );
          },
        }
      );
    };

    // Ignore blank addresses, as v1 did.
    const updateV3DeliveryAddress: CartFacadeActions['updateDeliveryAddress'] =
      ({ deliveryAddress }) => {
        if (!deliveryAddress.street?.length) return;
        updateLocalDeliveryAddress(deliveryAddress);
      };

    return {
      ...v1Actions,

      updateProduct,

      // Same order as `attemptToAddToCart`: store guard, then analytics.
      addProduct: ({
        almostGone,
        columnPosition,
        count,
        location,
        menuProduct,
        moeTrack,
        navigate,
        price_id,
        productLocation,
        rowPosition,
        source,
        special,
        store: addStore,
        userPreferences,
        userSegments,
        withoutDrawer = false,
      }) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        // No cart here means create, not a no-op.
        const cart = uuid ? cartSnapshot(uuid) : null;
        const storeAndProduct = prepareCartProduct(
          addStore,
          menuProduct,
          price_id,
          count
        );

        // Still Redux: `/replaceCart` reads the product back from the slice.
        if (cart && !isSameStoreId(cart.store.id, addStore.id)) {
          return maybeChangeStore(appMode, dispatch, storeAndProduct, navigate);
        }

        // Matches v1's `isProductInCart` (on `id`, not `product_id`).
        const productInCart = cart?.products.some(
          (product) =>
            product.id === menuProduct.id && product.price_id === price_id
        );

        if (productInCart) {
          trackCartEvent({
            count,
            event: EventNames.UpdatedProductInCart,
            location,
            menuProduct,
            price_id,
            productLocation,
            source,
            special,
            store: addStore,
            userPreferences,
            userSegments,
          });
          updateProduct(
            { count, price_id, product_id: menuProduct.id },
            moeTrack
          );
          return;
        }

        const basePrice = Number(menuProduct[`price_${price_id}`]);
        const discountedPrice = Number(
          menuProduct[`discounted_price_${price_id}`]
        );
        postAnalyticsEvent({
          name: 'cartItemAdd',
          properties: {
            product: {
              price_id,
              ...pick(menuProduct, [
                'product_id',
                'name',
                'brand',
                'category',
                'kind',
              ]),
            },
            productId: menuProduct.product_id || menuProduct.id,
            quantity: count,
            ...(basePrice > 0 && { price: basePrice }),
            ...(discountedPrice > 0 && { discountedPrice }),
          },
        });

        trackCartEvent({
          almostGone,
          cartId: cart?.id,
          columnPosition,
          count,
          event: EventNames.AddedProductToCart,
          location,
          menuProduct,
          price_id,
          productLocation,
          rowPosition,
          source,
          special,
          store: addStore,
          userPreferences,
          userSegments,
        });

        const onAdded = (cartUuid: string) => {
          moeTrack(
            moeAddToBag({
              cartUuid,
              product: storeAndProduct.product,
              storeId: addStore.id,
              storeName: addStore.name,
            })
          );

          openDrawerAfterAdd(withoutDrawer);
        };

        // Send `menuProduct.id`: grid cards omit `product_id`, as v1 did.
        if (!uuid) {
          return createV3Cart(
            [{ count, price_id, product_id: menuProduct.id }],
            addStore.id,
            (created) => onAdded(created.uuid)
          );
        }

        mutateAddCartProduct(
          {
            appMode,
            cartUuid: uuid,
            deviceId: janeDeviceId,
            jwt: getJwt(),
            product: {
              count,
              price_id,
              product_id: menuProduct.id,
              // Never saved by the API; sent to match v1's payload.
              recommended: !!menuProduct.recommended,
            },
            storeId: addStore.id,
            tags: cartTags,
          },
          { onSuccess: (data) => onAdded(data.cart_uuid) }
        );
      },

      // A second create would orphan the existing cart.
      createCart: () => {
        if (getLocalCartUuid(appMode, storeId) || !store) return;
        createV3Cart([], store.id);
      },

      removeProduct: (productId, priceId) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        const cart = uuid ? cartSnapshot(uuid) : null;
        if (!uuid || !cart) return;

        const product = cart.products.find(
          (candidate) =>
            candidate.id === productId && candidate.price_id === priceId
        );
        // v1's `cartEmptied` rule, not a guess at whether it's destroyed.
        const wasOnlyLine = cart.products.length === 1;

        if (wasOnlyLine && cart.products[0].id === productId) {
          closeCartDrawer();
        }

        if (product) {
          track({
            event: EventNames.RemovedProductFromCart,
            productBrand: product.brand,
            productCategory: product.category,
            productId: productId.toString(),
            productName: product.name,
            // Base price, no special, as v1 did.
            productPrice: getActualUnitPrice(product, product.price_id) || 0,
            quantity: product.count,
            storeCurrency: store?.currency_code,
          });
        }

        const basePrice = product ? Number(product[`price_${priceId}`]) : 0;
        const discountedPrice = product
          ? Number(product[`discounted_price_${priceId}`])
          : 0;

        mutateRemoveCartProduct(
          {
            appMode,
            cartUuid: uuid,
            jwt: getJwt(),
            priceId,
            productId: Number(productId),
          },
          {
            onSuccess: () => {
              postAnalyticsEvent({
                name: 'cartItemRemoval',
                properties: {
                  priceId,
                  productId,
                  ...(basePrice > 0 && { price: basePrice }),
                  ...(discountedPrice > 0 && { discountedPrice }),
                },
              });

              if (wasOnlyLine) postAnalyticsEvent({ name: 'cartEmptied' });

              settleAfterRemoval(uuid);
            },
          }
        );
      },

      updateReservationMode: ({ reservationMode, userGroupSpecialId }) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        const deliveryAddress = activeAddress();
        const queryKey = cartKeys.detail(uuid, undefined, deliveryAddress);
        const previousCart = queryClient.getQueryData<Cart>(queryKey);
        // Stop in-flight refetches from restoring the old mode. Sync; no await.
        queryClient.cancelQueries({ queryKey: cartKeys.detail(uuid) });
        // Optimistic: the toggle can't wait for the PATCH. Undone on error.
        if (previousCart) {
          queryClient.setQueryData<Cart>(queryKey, {
            ...previousCart,
            reservation_mode: reservationMode,
          });
        }

        mutateUpdateCart(
          {
            appMode,
            deliveryAddress,
            jwt: getJwt(),
            reservationMode,
            userGroupSpecialId,
            uuid,
          },
          {
            onError: () => {
              // After a double toggle the snapshot is a guess too; refetch.
              if (previousCart) {
                queryClient.setQueryData(queryKey, previousCart);
              }
              queryClient.invalidateQueries({ queryKey });
            },
          }
        );
      },

      updateGroupSpecial: (groupSpecialId) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        mutateUpdateCart({
          appMode,
          deliveryAddress: activeAddress(),
          jwt: getJwt(),
          userGroupSpecialId: groupSpecialId,
          uuid,
        });
      },

      applyLoyalty: (crmParams) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        // Only the fields the v3 endpoint accepts.
        mutateAddLoyalty(
          {
            appMode,
            cartUuid: uuid,
            crmMemberPoints: crmParams.crm_member_points,
            crmProvider: crmParams.crm_provider,
            crmRewardId: String(crmParams.crm_reward_id),
            jwt: getJwt(),
            pointValue: crmParams.point_value,
            redemptionChecksum: crmParams.redemption_checksum,
            redemptionUrl: crmParams.redemption_url,
            reward: crmParams.reward,
            rewardName: crmParams.reward_name,
          },
          {
            onError: (error) => {
              const rejected =
                error instanceof ApiError && error.response.status === 422;

              NotificationsService.error(
                rejected ? error.message : CRM_REDEMPTION_ERROR_MESSAGE
              );
              trackError(error, {
                cartUuid: uuid,
                crmProvider: crmParams.crm_provider,
                crmRewardId: crmParams.crm_reward_id,
              });
            },
          }
        );
      },

      // v3 finds the redemption by id, so `crmProvider` is unused.
      removeLoyalty: (id) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        mutateRemoveLoyalty({
          appMode,
          cartUuid: uuid,
          jwt: getJwt(),
          redemptionId: Number(id),
        });
      },

      deleteUnavailableProducts: (products) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid || !products.length) return;

        // Close the drawer if no available products remain, as v1 did.
        if (!cartSnapshot(uuid)?.products.length) closeCartDrawer();

        // Delete the caller's rows; the cache may be stale here.
        const jwt = getJwt();

        // Raw API fn, not `useRemoveCartProduct`, to refetch once instead of
        // per delete. `allSettled` so one failure doesn't stop the rest.
        Promise.allSettled(
          products.map((product) =>
            removeCartProduct({
              appMode,
              cartUuid: uuid,
              jwt,
              priceId: product.price_id,
              productId: product.product_id,
            })
          )
        ).then((results) => {
          // v1 deleted in one request, so partial failure is new; log it.
          const rejected = results.filter(
            (result) => result.status === 'rejected'
          );
          if (rejected.length) {
            trackError(
              new Error('Failed to delete unavailable cart products'),
              {
                cartUuid: uuid,
                failed: rejected.length,
                reasons: rejected.map((result) => String(result.reason)),
                requested: products.length,
              }
            );
          }

          settleAfterRemoval(uuid);
        });
      },

      updateDeliveryAddress: updateV3DeliveryAddress,

      removeDeliveryAddress: () => updateLocalDeliveryAddress(null),

      // Identical flag-on; the two only differ flag-off.
      // TODO(carts-v3): merge into `updateDeliveryAddress` with the v1 path.
      updateHeadlessDeliveryAddress: updateV3DeliveryAddress,

      resetCart: () => {
        closeCartDrawer();
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        mutateDeleteCart(
          { appMode, jwt: getJwt(), uuid },
          { onSuccess: () => updateLocalCartUuid(appMode, storeId, null) }
        );
      },
    };
  }, [
    appMode,
    cartTags,
    closeCartDrawer,
    dispatch,
    janeDeviceId,
    mutateAddCartProduct,
    mutateAddLoyalty,
    mutateCreateCart,
    mutateDeleteCart,
    mutateRemoveCartProduct,
    mutateRemoveLoyalty,
    mutateUpdateCart,
    mutateUpdateCartProduct,
    openCartDrawer,
    queryClient,
    store,
    storeId,
    useV3Cart,
    v1CartProducts,
  ]);
};
